import React, { useState, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Typo, Flex, ListTable, Page, Icon, theme, Button, SimpleButton } from '../../ui';
import { useInvitations, useInvitesActions } from './hooks';
import { invitesApi } from './invitesApi';
import { useNavigate, useParams } from 'react-router';
import { enqueueSnackbar } from 'notistack';
import { TOAST_SUCCESS } from '../../utils/constants';
import InviteDetails from './components/InviteDetails';
import Loading from '../../ui/Loading';
import { useTranslation } from 'react-i18next';
import ConfirmDelete from '../../ui/Modal/ConfirmDelete';
import PageHeader from '../../ui/Page/PageHeader';
import PageControls from '../../ui/Page/PageControls';
import { useRefetchOnAuthChange } from '../auth/hooks';
import PageSection from '../../ui/Page/PageSection';

const Invites = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { inviteId } = useParams();

  const [searchQuery, setSearchQuery] = useState('');
  const [invitationToDelete, setInvitationToDelete] = useState(null);
  const [savedCurrentPage, setSavedCurrentPage] = useState(0);

  const { invitations: invitationsData, isLoading: isLoadingInvitations, refetch } = useInvitations({ searchText: searchQuery });
  useRefetchOnAuthChange(refetch);
  const { resendInvite, deleteInvite } = useInvitesActions();

  // TODO:
  // add sorting (by email and by status?) ?? we dont have sort in server so ?
  // filter by group and course
  // resend all invites or just selected ones

  const resendInviteHandler = useCallback(
    async (e, invite) => {
      e.preventDefault();
      e.stopPropagation();
      await resendInvite(invite.id);
    },
    [resendInvite],
  );

  const handleDeleteInvitation = useCallback(async () => {
    await deleteInvite(invitationToDelete.id);
    setInvitationToDelete(null);
  }, [deleteInvite, invitationToDelete]);

  const copyLink = useCallback(
    (e, invite) => {
      e.preventDefault();
      e.stopPropagation();

      navigator.clipboard.writeText(invite.url);
      enqueueSnackbar(t('invites.linkCopied'), { variant: TOAST_SUCCESS });
    },
    [t],
  );

  const tableInfo = [
    { label: t('globals.email'), data: (item) => item.email },
    // when user click in course details objects get updated and course comes as object
    { label: t('globals.course'), data: (item) => (item.course?.hasOwnProperty('name') ? item.course.name : item.course) },
    {
      label: t('invites.sentDate'),
      data: (item) => {
        const sentVal = item?.sent;
        return sentVal ? <Typo.Info $textAlign='center'>{sentVal}</Typo.Info> : '-';
      },
    },
    {
      label: t('invites.inviteLink'),
      data: (item) => (
        <Flex justify='center'>
          {!item.hasExpired ? (
            <Flex justify='space-between' width='70px'>
              <Icon.Valid size='small' color={theme.colors.SUCCESS_GREEN} />
              <SimpleButton icon='Copy' aria-label={t('invites.copyLink')} variant='ghost' size='small' onClick={(e) => copyLink(e, item)} />
            </Flex>
          ) : (
            <Flex justify='left' width='70px'>
              <Icon.NotValid size='small' />
            </Flex>
          )}
        </Flex>
      ),
    },
    {
      label: t('invites.resendInvitation'),
      data: (item) => (
        <Flex justify='center' width='100%'>
          <SimpleButton
            icon='SendInvitation'
            aria-label={t('invites.resendInvitation')}
            variant='ghost'
            size='small'
            onClick={(e) => resendInviteHandler(e, item)}
          />
        </Flex>
      ),
    },
    {
      label: t('globals.delete'),
      data: (item) => (
        <Flex justify='center' width='100%'>
          <SimpleButton
            icon='Delete'
            aria-label={t('globals.delete')}
            variant='ghost'
            size='small'
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setInvitationToDelete(item);
            }}
          />
        </Flex>
      ),
    },
  ];

  const handleSearch = useCallback((value) => {
    setSearchQuery(value);
  }, []);

  const openDialog = useCallback(() => {
    dispatch(
      invitesApi.util.updateQueryData('invitesModalState', undefined, (draft) => {
        if (draft) {
          draft.isInviteModalOpen = true;
          draft.modalProps = {};
        }
      }),
    );
  }, [dispatch]);

  const clearSearch = useCallback(() => {
    setSearchQuery('');
  }, [setSearchQuery]);

  const openInvite = useCallback(
    (obj) => {
      navigate(`${obj.id}`);
    },
    [navigate],
  );

  return (
    <Page
      gap={30}
      staticElements={
        <PageHeader title={t('invites.pendingInvitations')}>
          <PageControls
            onClearSearch={clearSearch}
            onSearch={handleSearch}
            placeholder={t('invites.searchPlaceholder')}
            disableClear={!searchQuery}
            openModelButton={
              <Button icon='Invite' onClick={openDialog}>
                {t('invites.inviteUser')}
              </Button>
            }
          />
        </PageHeader>
      }
    >
      {isLoadingInvitations ? (
        <Loading />
      ) : (
        <PageSection>
          <ListTable
            tableInfo={tableInfo}
            data={invitationsData}
            onRowClick={openInvite}
            tableName={'invitations'}
            keepTrackOfPage={setSavedCurrentPage}
            defaultStartPage={savedCurrentPage}
          />
          <InviteDetails resendInviteHandler={resendInviteHandler} copyLink={copyLink} inviteId={inviteId} />
        </PageSection>
      )}
      <ConfirmDelete
        onClose={setInvitationToDelete}
        onAcceptDelete={handleDeleteInvitation}
        open={!!invitationToDelete?.id}
        description={t('invites.confirmDeleteInvitation', { userEmail: invitationToDelete?.email })}
      />
    </Page>
  );
};

export default Invites;
